// timesheetUtils.js (updated for Saturday duration & confirmed out-of-zone suppression)

import { format, isAfter, isToday, getDay } from 'date-fns';

// Constants
export const TZ = 'Asia/Dubai';
export const DEFAULT_START_MIN = 9 * 60 + 30; // 09:30
export const EARLIEST_OFFSET = 120; // 2 hours before start_time

// Map day index to day name (date-fns getDay: 0=Sunday ... 6=Saturday)
const dayIndexToName = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

// ───────────────────────────────────────────────
// Geo utilities
// ───────────────────────────────────────────────
const toRad = (x) => (x * Math.PI) / 180;

export function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000; // meters
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export function isPointInCircle(lat, lng, zone) {
  const zLng = zone.lng != null ? zone.lng : zone.lon;
  return haversineDistance(lat, lng, zone.lat, zLng) <= Number(zone.radius || 0);
}

export function isPointInPolygon(lat, lng, path) {
  let inside = false;
  for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
    const [yi, xi] = path[i];
    const [yj, xj] = path[j];
    const intersect =
      (yi > lat) !== (yj > lat) &&
      lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export const isPointInAnyZone = (lat, lng, zones) => {
  const safeZones = Array.isArray(zones) ? zones : [];
  return safeZones.some((z) =>
    z.radius != null
      ? isPointInCircle(lat, lng, z)
      : Array.isArray(z.paths) && isPointInPolygon(lat, lng, z.paths)
  );
};

// ───────────────────────────────────────────────
// Time utilities
// ───────────────────────────────────────────────
export function parseStartTimes(str) {
  if (!str) return [DEFAULT_START_MIN];
  return str
    .split(',')
    .map((s) => {
      const [h, m] = s.trim().split(':').map(Number);
      return !isNaN(h) && !isNaN(m) ? h * 60 + m : null;
    })
    .filter((x) => x !== null)
    .sort((a, b) => a - b);
}

const parseHmToMinutes = (value) => {
  if (typeof value !== 'string') return null;
  const [h, m] = value.trim().split(':').map(Number);
  if (!Number.isInteger(h) || !Number.isInteger(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
};

const scheduleHasDay = (daysOfWeek, dayOfWeek) => {
  return (daysOfWeek || '')
    .split(',')
    .map((d) => d.trim())
    .filter(Boolean)
    .includes(dayOfWeek);
};

const normalizeScheduleFromRange = (startTimesValue, source) => {
  const [startRaw, endRaw] = String(startTimesValue || '').split(',');
  const start_time = (startRaw || '').trim();
  const end_time = (endRaw || '').trim();
  if (!start_time) return null;
  return {
    start_time,
    end_time: end_time || null,
    source,
  };
};

const buildEmployeeScheduleGroup = (member) => {
  const raw = String(member?.start_time || '').trim();
  if (!raw) return [];

  // Allow both "HH:MM" and "HH:MM,HH:MM" shapes.
  const parsed = normalizeScheduleFromRange(raw, 'employee');
  if (parsed) return [parsed];

  return [{ start_time: raw, end_time: null, source: 'employee' }];
};

const collectSchedulesForDay = (schedules, source, dayOfWeek) => {
  const matched = [];
  for (const sched of schedules || []) {
    if (!sched?.days_of_week) continue;
    if (!scheduleHasDay(sched.days_of_week, dayOfWeek)) continue;
    const normalized = normalizeScheduleFromRange(sched.start_times, source);
    if (normalized) matched.push(normalized);
  }
  return matched;
};

const getScheduleGroupsByPriority = (member, dayOfWeek) => {
  const groups = [];

  const employeeGroup = buildEmployeeScheduleGroup(member);
  if (employeeGroup.length) groups.push(employeeGroup);

  const scheduleSources = [
    ['team', member.team_schedules || []],
    ['department', member.department_schedules || []],
    ['organization', member.organization_schedules || []],
  ];

  for (const [source, schedules] of scheduleSources) {
    const matched = collectSchedulesForDay(schedules, source, dayOfWeek);
    if (matched.length) groups.push(matched);
  }

  if (!groups.length) {
    groups.push([{ start_time: '09:30', end_time: '18:30', source: 'default' }]);
  }
  return groups;
};

const pickBestScheduleForClockIn = (schedules, evMin) => {
  const parsed = (schedules || [])
    .map((sched) => {
      const startMin = parseHmToMinutes(sched.start_time);
      if (startMin === null) return null;
      return { ...sched, startMin };
    })
    .filter(Boolean);

  if (!parsed.length) return null;

  // Prefer schedules where clock-in is inside allowed window [start-120, start].
  const inWindow = parsed.filter((sched) => evMin >= (sched.startMin - EARLIEST_OFFSET) && evMin <= sched.startMin);
  const pool = inWindow.length ? inWindow : parsed;

  return pool
    .slice()
    .sort((a, b) => {
      const delta = Math.abs(evMin - a.startMin) - Math.abs(evMin - b.startMin);
      if (delta !== 0) return delta;
      return a.startMin - b.startMin;
    })[0];
};

const pickGroupWithExplicitEnd = (groups) => {
  for (const group of groups || []) {
    const hasEnd = group.some((sched) => parseHmToMinutes(sched.end_time) !== null);
    if (hasEnd) return group;
  }
  return (groups && groups[0]) || [];
};

export const calcHours = (recs) => {
  let sum = 0;
  for (let i = 0; i < recs.length; i++) {
    if (
      recs[i].event_type === 'clock_in' &&
      recs[i + 1]?.event_type === 'clock_out'
    ) {
      sum += (recs[i + 1].timestamp - recs[i].timestamp) / 3600;
      i++;
    }
  }
  return sum;
};

export const hFmt = (h) =>
  h ? `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m` : '';

export function fmtTime(ts) {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(ts * 1000));
}

// ───────────────────────────────────────────────
// Cell colors (UI helpers)
// ───────────────────────────────────────────────
export function dayBg(val, dObj) {
  if (isAfter(dObj, new Date())) return '#EEEEEE'; // Future
  if (isToday(dObj)) return '#FFF9C4'; // Today
  if (dObj.getDay() === 0) return '#E3F2FD'; // Sunday
  if (!val) return '#FFE6E6'; // Empty
  return '#E8F5E9'; // Normal
}

export const excelRgb = (val, dObj) => {
  const formattedVal = typeof val === 'number' ? hFmt(val) : val;
  return dayBg(formattedVal, dObj).replace('#', 'FF').toUpperCase();
};

// ───────────────────────────────────────────────
// Core: per-day aggregate
// outOfZone === true  ⇢ только если есть clock_in вне зоны И manager_confirmed === false
// ───────────────────────────────────────────────
export function cellInfo(member, date) {
  const iso = format(date, 'yyyy-MM-dd');
  const day = member.days.find((d) => d.date === iso) || { records: [] };
  const dayOfWeek = dayIndexToName[getDay(date)];
  const dayNum = getDay(date);

  const hrs = calcHours(day.records);
  const hrsFormatted = hFmt(hrs);

  let late = false;
  let outOfZone = false;
  let missedOut = false;
  let earlyLeave = false;
  let startTime = '09:30'; // default
  let selectedSchedule = null;

  if (day.records.length) {
    // все clock-in этого дня, по возрастанию времени
    const clockIns = day.records
      .filter((r) => r.event_type === 'clock_in')
      .sort((a, b) => a.timestamp - b.timestamp);

    const lastRec = day.records.at(-1);
    missedOut = clockIns.length > 0 && lastRec?.event_type === 'clock_in';

    if (clockIns.length) {
      const firstIn = clockIns[0];
      const [hh, mm] = fmtTime(firstIn.timestamp).split(':').map(Number);
      const evMin = hh * 60 + mm;

      // Выбор по приоритету источника: employee -> team -> department -> organization.
      // 1) Для start/late берём первый непустой источник.
      // 2) Для end/early — первый источник, где есть валидный end_time.
      const scheduleGroups = getScheduleGroupsByPriority(member, dayOfWeek);

      const startGroup = scheduleGroups[0] || [];
      selectedSchedule = pickBestScheduleForClockIn(startGroup, evMin);
      if (selectedSchedule) {
        startTime = selectedSchedule.start_time;
        const startMin = parseHmToMinutes(selectedSchedule.start_time);
        if (startMin !== null) {
          const earliest = startMin - EARLIEST_OFFSET;
          late = evMin > startMin || evMin < earliest;
        }
      }

      // ── Out-of-zone flag with confirmation logic ──
      // Предпочитаем серверные флаги, но если их нет — считаем по зонам
      const hasServerFlag = typeof firstIn.out_of_zone === 'boolean' || typeof firstIn.manager_confirmed === 'boolean';
      const computedOut = !isPointInAnyZone(firstIn.lat, firstIn.lon ?? firstIn.lng, member.zones);

      const isUnconfirmed =
        firstIn.manager_confirmed === false ||
        firstIn.manager_confirmed === 0 ||
        firstIn.manager_confirmed === null ||
        typeof firstIn.manager_confirmed === 'undefined';

      // Если сервер прислал out_of_zone:
      //  - outOfZone = true только если out_of_zone === true И запись не подтверждена (isUnconfirmed)
      // Иначе — используем вычисленный computedOut и ту же проверку isUnconfirmed.
      if (hasServerFlag) {
        outOfZone = firstIn.out_of_zone === true && isUnconfirmed;
      } else {
        outOfZone = computedOut && isUnconfirmed;
      }

      // ── Early leave ──
      if (!missedOut) {
        let scheduledEndTs = null;
        const endGroup = pickGroupWithExplicitEnd(scheduleGroups);
        const scheduleForEnd = pickBestScheduleForClockIn(endGroup, evMin) || selectedSchedule;
        const explicitEndMin = parseHmToMinutes(scheduleForEnd?.end_time);

        if (explicitEndMin !== null) {
          const endH = String(Math.floor(explicitEndMin / 60)).padStart(2, '0');
          const endM = String(explicitEndMin % 60).padStart(2, '0');
          const scheduledEndDate = new Date(`${iso}T${endH}:${endM}:00+04:00`);
          scheduledEndTs = Math.floor(scheduledEndDate.getTime() / 1000);
        } else {
          // Fallback for legacy schedules without explicit end_time.
          const scheduledStartStr = `${iso}T${startTime}:00+04:00`;
          const scheduledStartDate = new Date(scheduledStartStr);
          const scheduledStartTs = Math.floor(scheduledStartDate.getTime() / 1000);
          const durationHours = (dayNum === 6) ? 4 : 9; // Saturday=6 => 4h, else 9h
          scheduledEndTs = scheduledStartTs + durationHours * 3600;
        }

        if (lastRec.timestamp < scheduledEndTs) {
          earlyLeave = true;
        }
      }
    }
  }

  return { hrs, hrsFormatted, late, outOfZone, missedOut, earlyLeave, startTime };
}
